import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ImageResize from 'quill-image-resize-module-react';
import { API_URL } from '../utils/Functions';
import { primaryLinks } from '../components/Navbar';

// Register custom fonts
const Font = Quill.import('formats/font');
Font.whitelist = ['sans-serif', 'inter', 'outfit', 'roboto', 'playfair', 'merriweather', 'monospace'];
Quill.register(Font, true);

// Register font sizes
const Size = Quill.import('formats/size');
Size.whitelist = ['10px','12px','14px','16px','18px','20px','24px','28px','32px','36px','48px','64px'];
Quill.register(Size, true);

// Register image resize module
Quill.register('modules/imageResize', ImageResize, true);

// Custom horizontal rule blot
const BlockEmbed = Quill.import('blots/block/embed');
class DividerBlot extends BlockEmbed {
  static create() { return document.createElement('hr'); }
}
DividerBlot.blotName = 'divider';
DividerBlot.tagName = 'hr';
Quill.register(DividerBlot, true);

// Custom Button Embed
const InlineEmbed = Quill.import('blots/embed');
class ButtonEmbed extends InlineEmbed {
  static create(value) {
    const node = super.create();
    node.setAttribute('class', 'ap-btn-embed');
    node.setAttribute('contenteditable', 'false');
    
    let config = value;
    if (typeof config === 'string') {
      try { config = JSON.parse(config); } catch (e) { config = {}; }
    }
    
    node.dataset.btnConfig = JSON.stringify(config);
    
    const a = document.createElement('a');
    a.href = config.link || '#';
    a.innerText = config.text || 'Click Here';
    a.style.display = 'inline-block';
    a.style.padding = '12px 28px';
    a.style.background = 'linear-gradient(135deg, #CFA047 0%, #061830 100%)';
    a.style.color = '#fff';
    a.style.textDecoration = 'none';
    a.style.borderRadius = '50px';
    a.style.fontWeight = '600';
    a.style.fontFamily = "'Inter', sans-serif";
    a.style.margin = '4px 8px';
    a.style.boxShadow = '0 4px 12px rgba(207,160,71,0.3)';
    a.style.cursor = 'pointer';
    
    node.appendChild(a);
    return node;
  }
  static value(node) {
    if (node.dataset.btnConfig) {
      try { return JSON.parse(node.dataset.btnConfig); } catch (e) { return {}; }
    }
    return {};
  }
}
ButtonEmbed.blotName = 'btnEmbed';
ButtonEmbed.tagName = 'span';
ButtonEmbed.className = 'ap-btn-embed';
Quill.register(ButtonEmbed, true);

// Fallback for old customButton format so it doesn't crash
const Link = Quill.import('formats/link');
class OldButtonBlot extends Link {
  static create(value) {
    const node = super.create(value);
    node.setAttribute('class', 'ap-quill-btn-old');
    return node;
  }
}
OldButtonBlot.blotName = 'customButton';
OldButtonBlot.tagName = 'A';
OldButtonBlot.className = 'ap-quill-btn';
Quill.register(OldButtonBlot, true);

// Custom Card blot
class CardBlot extends BlockEmbed {
  static create(value) {
    const node = super.create();
    node.setAttribute('class', 'ap-quill-card');
    node.setAttribute('contenteditable', 'false');
    
    // Convert value string to object if necessary (Quill passes strings sometimes)
    let config = value;
    if (typeof config === 'string') {
      try { config = JSON.parse(config); } catch (e) { config = {}; }
    }
    
    node.dataset.cardConfig = JSON.stringify(config);
    
    const color = config.color || '#CFA047';
    node.style.width = config.width || '100%';
    node.style.minHeight = config.height || 'auto';
    node.style.border = `1px solid ${color}40`;
    node.style.borderTop = `4px solid ${color}`;
    node.style.borderRadius = '12px';
    node.style.padding = '24px';
    node.style.margin = '16px 0';
    node.style.backgroundColor = '#ffffff';
    node.style.boxShadow = '0 4px 16px rgba(0,0,0,0.04)';
    node.style.display = 'inline-flex';
    node.style.flexDirection = 'column';
    node.style.gap = '12px';
    node.style.fontFamily = "'Inter', sans-serif";
    node.style.verticalAlign = 'top';
    node.style.cursor = 'pointer'; // indicates it can be interacted with in editor
    
    if (config.icon) {
      const iconWrap = document.createElement('div');
      iconWrap.style.width = '48px';
      iconWrap.style.height = '48px';
      iconWrap.style.borderRadius = '12px';
      iconWrap.style.background = `${color}15`;
      iconWrap.style.color = color;
      iconWrap.style.display = 'flex';
      iconWrap.style.alignItems = 'center';
      iconWrap.style.justifyContent = 'center';
      iconWrap.style.fontSize = '20px';
      iconWrap.style.marginBottom = '8px';
      
      const iconObj = document.createElement('i');
      iconObj.setAttribute('class', config.icon);
      iconWrap.appendChild(iconObj);
      node.appendChild(iconWrap);
    }
    
    if (config.title) {
      const title = document.createElement('h4');
      title.innerText = config.title;
      title.style.margin = '0';
      title.style.fontSize = '18px';
      title.style.fontWeight = '700';
      title.style.color = '#1e293b';
      node.appendChild(title);
    }
    
    if (config.desc) {
      const desc = document.createElement('p');
      desc.innerText = config.desc;
      desc.style.margin = '0';
      desc.style.fontSize = '14.5px';
      desc.style.color = '#475569';
      desc.style.lineHeight = '1.6';
      node.appendChild(desc);
    }
    
    return node;
  }
  
  static value(node) {
    if (node.dataset.cardConfig) {
      try { return JSON.parse(node.dataset.cardConfig); } catch (e) { return {}; }
    }
    return {};
  }
}
CardBlot.blotName = 'customCard';
CardBlot.tagName = 'div';
CardBlot.className = 'ap-quill-card';
Quill.register(CardBlot, true);

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }, { font: Font.whitelist }, { size: Size.whitelist }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    [{ align: [] }],
    ['link', 'image', 'video'],
    ['blockquote', 'code-block', 'clean'],
  ],
  history: { delay: 500, maxStack: 100, userOnly: true },
  imageResize: {
    parchment: Quill.import('parchment'),
    modules: ['Resize', 'DisplaySize', 'Toolbar'],
  },
};

const AdminPages = () => {
  const [pages, setPages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const filteredPages = pages.filter(p => 
    (p.title || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
    (p.slug || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.menu_title || '').toLowerCase().includes(searchTerm.toLowerCase())
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('list');
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [modalConfig, setModalConfig] = useState({ isOpen: false, type: 'confirm', title: '', message: '', onConfirm: null });

  const showAlert = (title, message) => {
    setModalConfig({ isOpen: true, type: 'alert', title, message, onConfirm: null });
  };
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('menu');
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [isHtmlPreview, setIsHtmlPreview] = useState(false); // read-only rendered preview of HTML code
  const [isFullscreen, setIsFullscreen] = useState(false);
  const hasComplexHtmlRef = useRef(false); // tracks if content has complex HTML that Quill may strip
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [showTablePicker, setShowTablePicker] = useState(false);
  const [tableHover, setTableHover] = useState({ r: 0, c: 0 });
  const [showFindReplace, setShowFindReplace] = useState(false);
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');

  // Available forms and packages for the picker
  const [availableForms, setAvailableForms] = useState([]);
  const [availablePackages, setAvailablePackages] = useState([]);
  const dragFormId = useRef(null);
  
  // Card Builder state
  const [showCardModal, setShowCardModal] = useState(false);
  const [editingCardIndex, setEditingCardIndex] = useState(null);
  const [cardData, setCardData] = useState({ icon: 'fa-solid fa-star', title: 'New Feature', desc: 'Enter description here.', color: '#CFA047', width: '300px', height: 'auto' });

  // Button Builder state
  const [showBtnModal, setShowBtnModal] = useState(false);
  const [editingBtnIndex, setEditingBtnIndex] = useState(null);
  const [btnData, setBtnData] = useState({ text: 'Click Here', link: '#' });
  
  const quillRef = useRef(null);

  // Word / char count
  const updateCounts = useCallback((html) => {
    const text = html.replace(/<[^>]*>/g, '').trim();
    setCharCount(text.length);
    setWordCount(text ? text.split(/\s+/).filter(Boolean).length : 0);
  }, []);

  // Insert image at current cursor position from a File object
  const insertImageFile = useCallback((file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const quill = quillRef.current?.getEditor();
      if (!quill) return;
      const range = quill.getSelection(true);
      const idx = range ? range.index : quill.getLength();
      quill.insertEmbed(idx, 'image', e.target.result);
      quill.setSelection(idx + 1);
    };
    reader.readAsDataURL(file);
  }, []);

  // Insert table
  const insertTable = useCallback((rows, cols) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const range = quill.getSelection(true);
    const idx = range ? range.index : quill.getLength();
    let tableHtml = '<table border="1" style="border-collapse:collapse;width:100%;margin:12px 0">';
    for (let r = 0; r < rows; r++) {
      tableHtml += '<tr>';
      for (let c = 0; c < cols; c++) {
        tableHtml += r === 0
          ? '<th style="padding:8px 12px;background:#f0f4f8;border:1px solid #dde3ea;font-weight:600">Header</th>'
          : '<td style="padding:8px 12px;border:1px solid #dde3ea">&nbsp;</td>';
      }
      tableHtml += '</tr>';
    }
    tableHtml += '</table><p><br></p>';
    quill.clipboard.dangerouslyPasteHTML(idx, tableHtml);
    setShowTablePicker(false);
  }, []);

  // Insert divider
  const insertDivider = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const range = quill.getSelection(true);
    const idx = range ? range.index : quill.getLength();
    quill.insertEmbed(idx, 'divider', true);
    quill.setSelection(idx + 1);
  }, []);

  // Insert Button Embed
  const insertButtonEmbed = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    
    if (editingBtnIndex !== null) {
      quill.deleteText(editingBtnIndex, 1);
      quill.insertEmbed(editingBtnIndex, 'btnEmbed', btnData);
      quill.setSelection(editingBtnIndex + 1);
    } else {
      const range = quill.getSelection(true);
      const idx = range ? range.index : quill.getLength();
      quill.insertEmbed(idx, 'btnEmbed', btnData);
      quill.setSelection(idx + 1);
    }
    setShowBtnModal(false);
    setEditingBtnIndex(null);
  }, [btnData, editingBtnIndex]);

  // Insert Custom Card
  const insertCardEmbed = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    
    if (editingCardIndex !== null) {
      // Editing existing card
      quill.deleteText(editingCardIndex, 1);
      quill.insertEmbed(editingCardIndex, 'customCard', cardData);
      quill.setSelection(editingCardIndex + 1);
    } else {
      // Inserting new card
      const range = quill.getSelection(true);
      const idx = range ? range.index : quill.getLength();
      quill.insertEmbed(idx, 'customCard', cardData);
      quill.setSelection(idx + 1);
    }
    setShowCardModal(false);
    setEditingCardIndex(null);
  }, [cardData, editingCardIndex]);

  // Handle double-click on cards to edit
  useEffect(() => {
    if (view !== 'editor') return;
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const root = quill.root;
    
    const handleDblClick = (e) => {
      const cardNode = e.target.closest('.ap-quill-card');
      if (cardNode) {
        const blot = Quill.find(cardNode);
        if (blot) {
          const index = quill.getIndex(blot);
          const config = JSON.parse(cardNode.dataset.cardConfig || '{}');
          setCardData({
            icon: config.icon || 'fa-solid fa-star',
            title: config.title || '',
            desc: config.desc || '',
            color: config.color || '#CFA047',
            width: config.width || '100%',
            height: config.height || 'auto'
          });
          setEditingCardIndex(index);
          setShowCardModal(true);
          return;
        }
      }

      const btnNode = e.target.closest('.ap-btn-embed');
      if (btnNode) {
        const blot = Quill.find(btnNode);
        if (blot) {
          const index = quill.getIndex(blot);
          const config = JSON.parse(btnNode.dataset.btnConfig || '{}');
          setBtnData({
            text: config.text || 'Click Here',
            link: config.link || '#'
          });
          setEditingBtnIndex(index);
          setShowBtnModal(true);
        }
      }
    };
    
    root.addEventListener('dblclick', handleDblClick);
    return () => root.removeEventListener('dblclick', handleDblClick);
  }, [view]);

  // When editor opens with existing HTML content, force Quill to render it properly as design
  useEffect(() => {
    if (view !== 'editor' || isHtmlMode) return;
    const timer = setTimeout(() => {
      const quill = quillRef.current?.getEditor();
      if (quill && formData.content) {
        // Only inject if Quill's current content doesn't match (avoids overwriting user edits)
        const currentHtml = quill.root.innerHTML;
        if (currentHtml === '<p><br></p>' || currentHtml === '') {
          quill.clipboard.dangerouslyPasteHTML(0, formData.content);
        }
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toolbar button handler helpers

  const quillExec = useCallback((cmd) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    quill.focus();
    if (cmd === 'undo') quill.history.undo();
    if (cmd === 'redo') quill.history.redo();
  }, []);

  // Find & replace
  const handleFindReplace = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill || !findText) return;
    const text = quill.getText();
    const idx = text.indexOf(findText);
    if (idx !== -1) {
      quill.setSelection(idx, findText.length);
      if (replaceText !== undefined && replaceText !== '') {
        quill.deleteText(idx, findText.length);
        quill.insertText(idx, replaceText);
      }
    }
  }, [findText, replaceText]);

  const defaultForm = {
    title: '', slug: '', menu_title: '', show_in_menu: true,
    sort_order: 0, banner_title: '', banner_subtitle: '', content: '',
    custom_form_id: '', price_package_ids: [],
    meta_title: '', meta_description: '', meta_keywords: '', is_published: true
  };
  const [formData, setFormData] = useState(defaultForm);

  const fetchPages = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/pages`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      const res = await response.json();
      if (!response.ok) throw res;
      setPages(res.data?.data || []);
    } catch (err) {
      setError('Failed to load pages.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPages(); }, []);

  // Fetch forms and packages list when editor opens
  useEffect(() => {
    if (view !== 'editor') return;
    const token = localStorage.getItem('auth_token');
    fetch(`${API_URL}/api/admin/forms`, {
      headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
    })
      .then(r => r.json())
      .then(res => setAvailableForms(res.data?.data || res.data || []))
      .catch(() => {});

    fetch(`${API_URL}/api/admin/packages`, {
      headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
    })
      .then(r => r.json())
      .then(res => setAvailablePackages(res.data?.data || res.data || []))
      .catch(() => {});
  }, [view]);

  const handleOpenCreate = () => {
    setFormData(defaultForm);
    setIsEditing(false);
    setCurrentId(null);
    setActiveSection('menu');
    setView('editor');
  };

  const handleOpenEdit = (page) => {
    setFormData({
      title: page.title || '', slug: page.slug || '',
      menu_title: page.menu_title || '', show_in_menu: !!page.show_in_menu,
      sort_order: page.sort_order || 0, banner_title: page.banner_title || '',
      banner_subtitle: page.banner_subtitle || '', content: page.content || '',
      custom_form_id: page.custom_form_id || '', price_package_ids: page.price_package_ids || [],
      meta_title: page.meta_title || '', meta_description: page.meta_description || '',
      meta_keywords: page.meta_keywords || '', is_published: !!page.is_published,
    });
    setIsEditing(true);
    setCurrentId(page.id);
    setActiveSection('menu');

    // Auto-detect if content has complex HTML (inline styles, divs, custom markup)
    // If so, open directly in HTML mode to prevent Quill from stripping the design.
    const content = page.content || '';
    const hasComplexHtml = /style\s*=|<div|<section|<table|<span\s+style|<a\s+style|<script|<iframe/.test(content);
    hasComplexHtmlRef.current = hasComplexHtml;
    setIsHtmlMode(hasComplexHtml);

    setView('editor');
  };

  const confirmDelete = (page) => {
    setItemToDelete(page);
  };

  const executeDelete = async () => {
    if (!itemToDelete) return;
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/admin/pages/${itemToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!res.ok) throw new Error();
      setPages(pages.filter(p => p.id !== itemToDelete.id));
      setItemToDelete(null);
      toast.success('Page deleted successfully!');
    } catch { toast.error('Failed to delete the page.'); }
  };

  const handleSave = async (publishStatus = null) => {
    if (!formData.title.trim()) { toast.error('Page Title is required.'); return; }
    if (!formData.menu_title) {
      toast.error('Menu Title is required. Please select a category from the Menu tab.');
      setActiveSection('menu');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const url = isEditing ? `${API_URL}/api/admin/pages/${currentId}` : `${API_URL}/api/admin/pages`;

      // Always capture the latest Quill HTML directly before saving (in visual mode).
      // This ensures deletions of embedded elements (package links, form embeds, etc.)
      // are correctly reflected rather than relying on formData which may be stale.
      // In HTML mode or preview mode, always use the raw formData.content (textarea value).
      let latestContent = formData.content;
      if (!isHtmlMode && !isHtmlPreview) {
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const liveHtml = quill.root.innerHTML;
          latestContent = liveHtml === '<p><br></p>' ? '' : liveHtml;
        }
      }

      const payload = { ...formData, content: latestContent, ...(publishStatus !== null && { is_published: publishStatus }) };
      
      // Sanitize payload: strip lone Unicode surrogates that break JSON parsing on the server
      const stripSurrogates = (str) => {
        if (typeof str !== 'string') return str;
        return str.replace(/[\uD800-\uDFFF]/g, '');
      };
      if (payload.content) payload.content = stripSurrogates(payload.content);
      if (payload.custom_form_id === '') payload.custom_form_id = null;
      if (payload.parent_id === '') payload.parent_id = null;
      if (Array.isArray(payload.price_package_ids) && payload.price_package_ids.length === 0) payload.price_package_ids = null;
      if (payload.sort_order === '' || payload.sort_order === null || isNaN(payload.sort_order)) payload.sort_order = 0;
      else payload.sort_order = Number(payload.sort_order);
      
      const res = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw data;
      setView('list');
      fetchPages();
      toast.success(isEditing ? 'Page updated successfully!' : 'Page created successfully!');
    } catch (err) {
      let errorMsg = err.message || 'Error saving page.';
      if (err.errors) {
        const firstError = Object.values(err.errors)[0][0];
        errorMsg = firstError;
      }
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'title' && !isEditing) {
      const autoSlug = value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
      setFormData(prev => ({ ...prev, title: value, slug: autoSlug }));
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  if (view === 'editor') {

    return (
        <div className={`pg-editor-wrap${isFullscreen ? ' pg-fullscreen' : ''}`}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

          .pg-editor-wrap { display: flex; flex-direction: column; height: calc(100vh - 64px); background: #f0f4f8; margin: -24px; font-family: 'Inter', sans-serif; }

          /* ── Top Bar ─────────────────────────────── */
          .pg-topbar {
            display: flex; align-items: center; gap: 16px;
            padding: 0 20px; height: 60px;
            background: #ffffff;
            border-bottom: 1px solid #dde3ea;
            box-shadow: 0 1px 4px rgba(0,0,0,0.06);
          }
          .pg-back-btn {
            display: flex; align-items: center; justify-content: center;
            width: 36px; height: 36px; border-radius: 8px;
            border: 1px solid #dde3ea; background: #f7f9fc;
            color: #6b7a90; cursor: pointer; transition: all 0.18s; flex-shrink: 0;
          }
          .pg-back-btn:hover { background: #e8edf3; color: #2d3a4b; border-color: #bcc7d4; }
          .pg-title-input {
            flex: 1; border: none; outline: none; background: transparent;
            font-size: 17px; font-weight: 600; color: #1a2332; font-family: 'Inter', sans-serif;
            padding: 0;
          }
          .pg-title-input::placeholder { color: #bcc7d4; font-weight: 400; }
          .pg-topbar-actions { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
          .pg-status-pill {
            display: flex; align-items: center; gap: 6px;
            padding: 6px 12px; border-radius: 20px; font-size: 12px; font-weight: 600;
          }
          .pg-status-pill.pub { background: #fdf5e6; color: #a67f35; }
          .pg-status-pill.draft { background: #fff7e6; color: #b06c00; }
          .pg-status-pill .dot { width: 7px; height: 7px; border-radius: 50%; }
          .pg-status-pill.pub .dot { background: #CFA047; }
          .pg-status-pill.draft .dot { background: #f59e0b; }

          .pg-btn-draft {
            padding: 8px 18px; border-radius: 8px; font-size: 13px; font-weight: 600;
            border: 1.5px solid #dde3ea; background: #ffffff; color: #4a5568;
            cursor: pointer; transition: all 0.18s; font-family: 'Inter', sans-serif;
          }
          .pg-btn-draft:hover { background: #f7f9fc; border-color: #bcc7d4; color: #1a2332; }
          .pg-btn-publish {
            padding: 8px 20px; border-radius: 8px; font-size: 13px; font-weight: 600;
            border: none; cursor: pointer; font-family: 'Inter', sans-serif;
            background: linear-gradient(135deg, #CFA047 0%, #061830 100%);
            color: white; box-shadow: 0 2px 8px rgba(207, 160, 71,0.3);
            transition: all 0.18s;
          }
          .pg-btn-publish:hover { box-shadow: 0 4px 14px rgba(207, 160, 71,0.4); transform: translateY(-1px); }
          .pg-btn-publish:disabled, .pg-btn-draft:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }

          /* ── Workspace ───────────────────────────── */
          .pg-workspace { display: flex; flex: 1; overflow: hidden; }
          .pg-fullscreen .pg-workspace { overflow: auto; }

          .pg-editor-col { flex: 1; overflow-y: auto; padding: 20px 24px; display: flex; flex-direction: column; gap: 16px; }
          .pg-fullscreen .pg-editor-col { padding: 28px 60px; max-width: 900px; margin: 0 auto; width: 100%; box-sizing: border-box; }

          /* Card wrapper for Quill */
          .pg-editor-card {
            background: #ffffff; border-radius: 14px;
            border: 1px solid #dde3ea;
            box-shadow: 0 2px 12px rgba(0,0,0,0.05);
            display: flex; flex-direction: column; overflow: visible;
          }

          /* Premium Quill toolbar override */
          .pg-editor-card .ql-toolbar.ql-snow {
            border: none;
            border-bottom: 1px solid #eef1f5;
            background: linear-gradient(to bottom, #ffffff, #f9fbfd);
            padding: 10px 16px;
            border-radius: 14px 14px 0 0;
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 2px;
            position: sticky; top: 0; z-index: 10;
          }
          .pg-editor-card .ql-toolbar.ql-snow .ql-formats {
            margin-right: 6px;
            display: flex;
            align-items: center;
          }
          .pg-editor-card .ql-snow .ql-picker-label {
            border: 1px solid #e2e8f0;
            border-radius: 6px;
            background: #f7f9fc;
            padding: 3px 8px;
            font-size: 12px;
            font-weight: 500;
            color: #4a5568;
            transition: all 0.15s;
          }
          .pg-editor-card .ql-snow .ql-picker-label:hover {
            border-color: #CFA047;
            color: #CFA047;
            background: #fdf5e6;
          }
          .pg-editor-card .ql-snow.ql-toolbar button {
            width: 28px;
            height: 28px;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.15s;
            padding: 4px;
          }
          .pg-editor-card .ql-snow.ql-toolbar button:hover {
            background: #fdf5e6;
            color: #CFA047 !important;
          }
          .pg-editor-card .ql-snow.ql-toolbar button svg path,
          .pg-editor-card .ql-snow.ql-toolbar button svg line,
          .pg-editor-card .ql-snow.ql-toolbar button svg polyline,
          .pg-editor-card .ql-snow.ql-toolbar button svg rect,
          .pg-editor-card .ql-snow.ql-toolbar button svg circle {
            stroke: currentColor;
          }
          .pg-editor-card .ql-snow.ql-toolbar button.ql-active {
            background: linear-gradient(135deg, #fdf5e6, #fef9f0);
            color: #CFA047 !important;
            border-color: #e8cc94;
          }
          .pg-editor-card .ql-snow .ql-picker.ql-expanded .ql-picker-options {
            border-radius: 8px;
            border: 1px solid #dde3ea;
            box-shadow: 0 8px 24px rgba(0,0,0,0.1);
            padding: 4px;
          }
          .pg-editor-card .ql-snow .ql-picker-options .ql-picker-item:hover {
            background: #fdf5e6;
            color: #CFA047;
            border-radius: 4px;
          }

          /* Premium custom action bar */
          .pg-action-bar {
            display: flex;
            align-items: center;
            gap: 3px;
            padding: 8px 16px;
            background: linear-gradient(to bottom, #ffffff, #f9fbfd);
            border-bottom: 1px solid #eef1f5;
            flex-wrap: nowrap;
            overflow-x: auto;
            scrollbar-width: none;
          }
          .pg-action-bar::-webkit-scrollbar { display: none; }
          .pg-action-bar-right {
            margin-left: auto;
            display: flex;
            align-items: center;
            gap: 4px;
            flex-shrink: 0;
          }
          .pg-action-sep { width: 1px; height: 20px; background: #e2e8f0; margin: 0 6px; flex-shrink: 0; }

          /* Premium labeled action buttons */
          .pg-act-btn {
            display: inline-flex; align-items: center; justify-content: center;
            padding: 6px 12px; border-radius: 8px; font-size: 12px;
            font-weight: 600; cursor: pointer;
            border: 1px solid #e2e8f0;
            background: #ffffff;
            color: #475569;
            gap: 6px; white-space: nowrap;
            transition: all 0.2s cubic-bezier(0.34,1.56,0.64,1);
            font-family: 'Inter', sans-serif; line-height: 1;
            box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
            position: relative; overflow: hidden;
          }
          .pg-act-btn::before {
            content: ''; position: absolute; inset: 0;
            background: linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0) 60%);
            pointer-events: none;
          }
          .pg-act-btn:hover {
            background: linear-gradient(135deg, #fdf5e6 0%, #fffbf2 100%);
            border-color: #ddb96a;
            color: #a67c30;
            box-shadow: 0 4px 12px rgba(207,160,71,0.2), 0 1px 3px rgba(0,0,0,0.08);
            transform: translateY(-1px);
          }
          .pg-act-btn:active { transform: translateY(0px); box-shadow: 0 1px 3px rgba(207,160,71,0.15); }
          .pg-act-btn.active {
            background: linear-gradient(135deg, #fdf5e6, #fef9f0);
            border-color: #CFA047; color: #a67c30;
            box-shadow: 0 2px 8px rgba(207,160,71,0.25);
          }
          .pg-act-btn .btn-icon {
            width: 18px; height: 18px; border-radius: 5px;
            display: inline-flex; align-items: center; justify-content: center;
            font-size: 11px;
          }

          /* Specific icon accent colors */
          .pg-act-btn.tb-table .btn-icon { background: rgba(99,102,241,0.12); color: #6366f1; }
          .pg-act-btn.tb-table:hover .btn-icon { background: rgba(99,102,241,0.18); }
          .pg-act-btn.tb-divider .btn-icon { background: rgba(100,116,139,0.12); color: #64748b; }
          .pg-act-btn.tb-divider:hover .btn-icon { background: rgba(100,116,139,0.18); }
          .pg-act-btn.tb-button .btn-icon { background: rgba(16,185,129,0.12); color: #059669; }
          .pg-act-btn.tb-button:hover .btn-icon { background: rgba(16,185,129,0.18); }
          .pg-act-btn.tb-card .btn-icon { background: rgba(245,158,11,0.12); color: #d97706; }
          .pg-act-btn.tb-card:hover .btn-icon { background: rgba(245,158,11,0.18); }
          .pg-act-btn.tb-find .btn-icon { background: rgba(59,130,246,0.12); color: #2563eb; }
          .pg-act-btn.tb-find:hover .btn-icon { background: rgba(59,130,246,0.18); }
          .pg-act-btn.tb-html .btn-icon { background: rgba(15,23,42,0.08); color: #334155; }
          .pg-act-btn.tb-html:hover .btn-icon { background: rgba(15,23,42,0.14); }

          /* Undo/Redo icon-only buttons */
          .pg-act-btn-icon {
            display: inline-flex; align-items: center; justify-content: center;
            width: 30px; height: 30px; border-radius: 8px; font-size: 13px;
            cursor: pointer;
            border: 1px solid #e2e8f0;
            background: #ffffff;
            color: #64748b;
            transition: all 0.2s cubic-bezier(0.34,1.56,0.64,1);
            box-shadow: 0 1px 3px rgba(0,0,0,0.06);
          }
          .pg-act-btn-icon:hover {
            background: #f1f5f9; border-color: #bcc7d4; color: #1e293b;
            box-shadow: 0 3px 8px rgba(0,0,0,0.1);
            transform: translateY(-1px);
          }
          .pg-act-btn-icon:active { transform: translateY(0px); }
          .pg-act-btn-icon.active { background: linear-gradient(135deg, #fdf5e6, #fef9f0); border-color: #CFA047; color: #a67c30; }

          /* Quill toolbar integration */
          #quill-toolbar .ql-formats { margin-right: 8px; }
          #quill-toolbar button, #quill-toolbar .ql-picker-label { color: #4a5568; }
          #quill-toolbar button:hover, #quill-toolbar .ql-picker-label:hover { color: #CFA047; }
          #quill-toolbar .ql-active { color: #CFA047 !important; }
          .pg-tb-sep { width: 1px; height: 20px; background: #dde3ea; margin: 0 6px; flex-shrink: 0; }
          .pg-tb-btn {
            display: inline-flex; align-items: center; justify-content: center;
            padding: 4px 8px; border-radius: 5px; font-size: 12px;
            font-weight: 600; cursor: pointer; border: 1px solid transparent;
            background: none; color: #4a5568; gap: 4px; white-space: nowrap;
            transition: all 0.15s; font-family: 'Inter', sans-serif;
          }
          .pg-tb-btn:hover { background: #e8edf3; color: #1a2332; border-color: #dde3ea; }
          .pg-tb-btn.active { background: #fdf5e6; color: #CFA047; border-color: #e8cc94; }

          /* Table picker */
          .pg-table-picker-wrap { position: relative; display: inline-block; }
          .pg-table-picker {
            position: absolute; top: 110%; left: 0; z-index: 100;
            background: #fff; border: 1px solid #dde3ea; border-radius: 10px;
            padding: 12px; box-shadow: 0 8px 24px rgba(0,0,0,0.12);
          }
          .pg-table-grid { display: grid; grid-template-columns: repeat(8,22px); gap: 3px; }
          .pg-table-cell {
            width: 22px; height: 22px; border-radius: 3px;
            border: 1px solid #dde3ea; cursor: pointer; transition: all 0.1s;
          }
          .pg-table-cell.active { background: rgba(207,160,71,0.3); border-color: #CFA047; }

          /* Find & replace */
          .pg-find-bar {
            display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
            padding: 8px 16px; background: #fdf5e6; border-bottom: 1px solid #e8cc94;
            font-size: 13px; font-family: 'Inter', sans-serif;
          }
          .pg-find-inp {
            padding: 5px 10px; border-radius: 6px; border: 1px solid #dde3ea;
            font-size: 13px; outline: none; font-family: 'Inter', sans-serif;
            width: 160px;
          }
          .pg-find-inp:focus { border-color: #CFA047; }
          .pg-find-btn {
            padding: 5px 12px; border-radius: 6px; border: none;
            background: #CFA047; color: #fff; font-size: 12px; font-weight: 600;
            cursor: pointer; font-family: 'Inter', sans-serif;
          }

          /* Word count bar */
          .pg-wordcount {
            display: flex; align-items: center; gap: 16px; justify-content: flex-end;
            padding: 6px 20px; background: #f7f9fc; border-top: 1px solid #dde3ea;
            font-size: 11.5px; color: #8496a9; font-family: 'Inter', sans-serif;
            border-radius: 0 0 14px 14px;
          }

          .pg-editor-card .ql-container.ql-snow { border: none; font-family: 'Inter', sans-serif; font-size: 15.5px; }
          .pg-editor-card .ql-editor { padding: 28px 36px; min-height: 70vh; color: #1a2332; line-height: 1.85; overflow: visible; }
          .pg-editor-card .ql-editor img { max-width: 100%; height: auto; display: block; margin: 8px 0; }
          .pg-editor-card .ql-editor h1 { font-size: 2em; font-weight: 700; margin: 0.8em 0 0.4em; }
          .pg-editor-card .ql-editor h2 { font-size: 1.6em; font-weight: 700; margin: 0.8em 0 0.4em; }
          .pg-editor-card .ql-editor h3 { font-size: 1.3em; font-weight: 600; margin: 0.8em 0 0.4em; }
          .pg-editor-card .ql-editor p { margin-bottom: 0.8em; }
          .pg-editor-card .ql-editor ul, .pg-editor-card .ql-editor ol { padding-left: 1.5em; margin-bottom: 0.8em; }
          .pg-editor-card .ql-editor blockquote { border-left: 4px solid #CFA047; padding-left: 16px; color: #5a6a7e; margin: 1em 0; }
          .pg-editor-card .ql-editor pre { background: #0f172a; color: #e2e8f0; padding: 16px 20px; border-radius: 8px; font-family: monospace; font-size: 14px; overflow-x: auto; }
          .pg-editor-card .ql-editor hr { border: none; border-top: 2px solid #dde3ea; margin: 20px 0; }
          .pg-editor-card .ql-editor table { border-collapse: collapse; width: 100%; margin: 12px 0; }
          .pg-editor-card .ql-editor td, .pg-editor-card .ql-editor th { border: 1px solid #dde3ea; padding: 8px 12px; }
          .pg-editor-card .ql-editor th { background: #f0f4f8; font-weight: 600; }
          .pg-editor-card .ql-editor.ql-blank::before { color: #bcc7d4; font-style: normal; font-size: 15px; }

          /* Button Class */
          .pg-editor-card .ql-editor .ap-quill-btn {
            display: inline-block;
            padding: 12px 28px;
            background: linear-gradient(135deg, #CFA047 0%, #061830 100%);
            color: #fff !important;
            text-decoration: none;
            border-radius: 50px;
            font-weight: 600;
            font-family: 'Inter', sans-serif;
            margin: 12px 0;
            box-shadow: 0 4px 12px rgba(207,160,71,0.3);
            transition: all 0.3s;
          }

          /* Fullscreen */
          .pg-editor-wrap.pg-fullscreen { position: fixed; inset: 0; z-index: 9999; margin: 0; height: 100vh; background: #f0f4f8; }

          /* Drag overlay */
          .pg-drop-zone { position: relative; }
          .pg-drop-zone.dragging::after {
            content: '📷  Drop image here';
            position: absolute; inset: 0; z-index: 20;
            display: flex; align-items: center; justify-content: center;
            font-size: 18px; font-weight: 600; color: #CFA047;
            background: rgba(207,160,71,0.08);
            border: 2px dashed #CFA047; border-radius: 0 0 14px 14px;
            pointer-events: none;
          }

          .ql-font-inter { font-family: 'Inter', sans-serif; }
          .ql-font-outfit { font-family: 'Outfit', sans-serif; }
          .ql-font-roboto { font-family: 'Roboto', sans-serif; }
          .ql-font-playfair { font-family: 'Playfair Display', serif; }
          .ql-font-merriweather { font-family: 'Merriweather', serif; }
          .ql-font-monospace { font-family: monospace; }

          /* Font picker labels */
          .ql-snow .ql-picker.ql-font .ql-picker-label::before,
          .ql-snow .ql-picker.ql-font .ql-picker-item::before { content: 'Sans Serif'; }
          .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="sans-serif"]::before,
          .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="sans-serif"]::before { content: 'Sans Serif' !important; }
          .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="inter"]::before,
          .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="inter"]::before { content: 'Inter' !important; font-family: 'Inter', sans-serif; }
          .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="outfit"]::before,
          .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="outfit"]::before { content: 'Outfit' !important; font-family: 'Outfit', sans-serif; }
          .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="roboto"]::before,
          .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="roboto"]::before { content: 'Roboto' !important; font-family: 'Roboto', sans-serif; }
          .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="playfair"]::before,
          .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="playfair"]::before { content: 'Playfair' !important; font-family: 'Playfair Display', serif; }
          .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="merriweather"]::before,
          .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="merriweather"]::before { content: 'Merriweather' !important; font-family: 'Merriweather', serif; }
          .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="monospace"]::before,
          .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="monospace"]::before { content: 'Monospace' !important; font-family: monospace; }

          /* Size picker labels */
          .ql-snow .ql-picker.ql-size .ql-picker-label::before,
          .ql-snow .ql-picker.ql-size .ql-picker-item::before { content: 'Normal'; }
          .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="10px"]::before,
          .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="10px"]::before { content: '10px' !important; font-size: 10px; }
          .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="12px"]::before,
          .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before { content: '12px' !important; font-size: 12px; }
          .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="14px"]::before,
          .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="14px"]::before { content: '14px' !important; font-size: 14px; }
          .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="16px"]::before,
          .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="16px"]::before { content: '16px' !important; font-size: 16px; }
          .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="18px"]::before,
          .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before { content: '18px' !important; font-size: 18px; }
          .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="20px"]::before,
          .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="20px"]::before { content: '20px' !important; font-size: 20px; }
          .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="24px"]::before,
          .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="24px"]::before { content: '24px' !important; font-size: 22px; }
          .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="28px"]::before,
          .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="28px"]::before { content: '28px' !important; font-size: 22px; }
          .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="32px"]::before,
          .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="32px"]::before { content: '32px' !important; font-size: 22px; }
          .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="36px"]::before,
          .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="36px"]::before { content: '36px' !important; font-size: 22px; }
          .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="48px"]::before,
          .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="48px"]::before { content: '48px' !important; font-size: 22px; }
          .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="64px"]::before,
          .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="64px"]::before { content: '64px' !important; font-size: 22px; }

          /* Make size picker wider to show labels */
          .ql-snow .ql-picker.ql-size { width: 70px; }
          .ql-snow .ql-picker.ql-font { width: 110px; }
          .ql-snow .ql-picker.ql-size .ql-picker-options { min-width: 80px; max-height: 220px; overflow-y: auto; }
          .ql-snow .ql-picker.ql-font .ql-picker-options { min-width: 130px; }

          /* ── Right Settings Panel ─────────────────── */
          .pg-settings-panel {
            width: 320px; background: #ffffff;
            border-left: 1px solid #dde3ea;
            display: flex; flex-direction: column; flex-shrink: 0;
          }
          .pg-panel-tabs {
            display: flex; overflow-x: auto; border-bottom: 1px solid #dde3ea;
            padding: 0 12px; gap: 2px; scrollbar-width: none;
          }
          .pg-panel-tabs::-webkit-scrollbar { display: none; }
          .pg-panel-tab {
            display: flex; flex-direction: column; align-items: center; gap: 3px;
            padding: 10px 10px 8px; font-size: 10.5px; font-weight: 600; color: #8496a9;
            border: none; background: none; cursor: pointer;
            border-bottom: 2px solid transparent; margin-bottom: -1px;
            transition: all 0.18s; white-space: nowrap; font-family: 'Inter', sans-serif;
          }
          .pg-panel-tab i { font-size: 14px; }
          .pg-panel-tab:hover { color: #CFA047; }
          .pg-panel-tab.active { color: #CFA047; border-bottom-color: #CFA047; }

          .pg-panel-body { flex: 1; overflow-y: auto; padding: 20px 18px; }

          .pg-field-group { margin-bottom: 18px; }
          .pg-field-label {
            display: block; font-size: 12px; font-weight: 600;
            color: #5a6a7e; margin-bottom: 7px; letter-spacing: 0.2px;
          }
          .pg-field-hint { font-size: 11px; color: #9babbf; margin-top: 5px; line-height: 1.4; }

          .pg-inp {
            width: 100%; padding: 9px 12px; border-radius: 8px;
            border: 1.5px solid #dde3ea; font-size: 13px; font-family: 'Inter', sans-serif;
            color: #2d3a4b; outline: none; background: #f7f9fc;
            transition: border-color 0.18s, box-shadow 0.18s; box-sizing: border-box;
          }
          .pg-inp:focus { border-color: #CFA047; box-shadow: 0 0 0 3px rgba(207, 160, 71,0.12); background: #ffffff; }
          .pg-textarea { min-height: 80px; resize: vertical; line-height: 1.6; }

          /* Toggle switch */
          .pg-toggle-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
          .pg-toggle-label { font-size: 13px; color: #3d4f62; font-weight: 500; }
          .pg-switch { position: relative; width: 42px; height: 24px; flex-shrink: 0; }
          .pg-switch input { opacity: 0; width: 0; height: 0; }
          .pg-slider {
            position: absolute; inset: 0; background: #dde3ea; border-radius: 24px;
            cursor: pointer; transition: 0.2s;
          }
          .pg-slider:before {
            content: ''; position: absolute; width: 18px; height: 18px;
            left: 3px; top: 3px; background: white; border-radius: 50%; transition: 0.2s;
            box-shadow: 0 1px 4px rgba(0,0,0,0.18);
          }
          .pg-switch input:checked + .pg-slider { background: #CFA047; }
          .pg-switch input:checked + .pg-slider:before { transform: translateX(18px); }

          .pg-divider { height: 1px; background: #eef1f5; margin: 18px 0; }

          /* Form Picker */
          .fp-section-label { font-size: 11px; font-weight: 700; color: #8496a9; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
          .fp-drop-zone {
            min-height: 72px; border: 2px dashed #dde3ea; border-radius: 12px;
            display: flex; align-items: center; justify-content: center;
            transition: border-color 0.2s, background 0.2s;
            background: #f9fbfc; margin-bottom: 14px; padding: 10px;
          }
          .fp-drop-zone.drag-over { border-color: #CFA047; background: #fdf5e6; }
          .fp-drop-zone.has-form { justify-content: flex-start; padding: 10px 12px; }
          .fp-selected-card {
            display: flex; align-items: center; gap: 10px; flex: 1;
            background: linear-gradient(135deg, #fdf5e6, #fffbf2);
            border: 1.5px solid #e8cc94; border-radius: 10px;
            padding: 10px 14px;
          }
          .fp-selected-icon { width: 34px; height: 34px; border-radius: 8px; background: linear-gradient(135deg, #061830, #CFA047); display: flex; align-items: center; justify-content: center; color: white; font-size: 14px; flex-shrink: 0; }
          .fp-selected-info { flex: 1; min-width: 0; }
          .fp-selected-name { font-size: 13px; font-weight: 700; color: #1a2332; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .fp-selected-slug { font-size: 11px; color: #9babbf; font-family: monospace; }
          .fp-remove-btn { background: none; border: none; color: #9babbf; cursor: pointer; font-size: 16px; padding: 4px; border-radius: 6px; transition: color 0.15s; flex-shrink: 0; }
          .fp-remove-btn:hover { color: #ef4444; }
          .fp-dnd-hint { font-size: 12px; color: #9babbf; text-align: center; }
          .fp-dnd-hint i { font-size: 20px; display: block; margin-bottom: 4px; color: #dde3ea; }
          .fp-forms-list { display: flex; flex-direction: column; gap: 8px; max-height: 280px; overflow-y: auto; padding-right: 2px; }
          .fp-form-card {
            display: flex; align-items: center; gap: 10px;
            border: 1.5px solid #e8edf4; border-radius: 10px;
            padding: 10px 12px; background: #fff;
            cursor: grab; transition: all 0.18s; user-select: none;
          }
          .fp-form-card:hover { border-color: #CFA047; background: #fdf5e6; box-shadow: 0 2px 8px rgba(207,160,71,0.12); }
          .fp-form-card:active { cursor: grabbing; }
          .fp-form-card.selected { border-color: #CFA047; background: #fdf5e6; }
          .fp-form-card-icon { width: 30px; height: 30px; border-radius: 7px; background: linear-gradient(135deg, #061830, #CFA047); display: flex; align-items: center; justify-content: center; color: white; font-size: 12px; flex-shrink: 0; }
          .fp-form-card-info { flex: 1; min-width: 0; }
          .fp-form-card-name { font-size: 13px; font-weight: 600; color: #1a2332; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .fp-form-card-meta { font-size: 11px; color: #9babbf; margin-top: 1px; }
          .fp-form-card-drag { color: #c8d4e0; font-size: 12px; flex-shrink: 0; }

          /* Skeleton loader for table */
          .pg-skeleton { height: 16px; border-radius: 6px; background: linear-gradient(90deg, #f0f4f8 25%, #e4eaf1 50%, #f0f4f8 75%); background-size: 200% 100%; animation: pg-shimmer 1.4s infinite; }
          @keyframes pg-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        `}</style>

        <div className="pg-topbar">
          <button className="pg-back-btn" onClick={() => setView('list')} title="Back to Pages">
            <i className="fa-solid fa-arrow-left" style={{ fontSize: '14px' }}></i>
          </button>


          <input
            type="text"
            className="pg-title-input"
            placeholder="Untitled Page"
            name="title"
            value={formData.title}
            onChange={handleChange}
          />

          <div className="pg-topbar-actions">
            <div className={`pg-status-pill ${formData.is_published ? 'pub' : 'draft'}`}>
              <span className="dot"></span>
              {formData.is_published ? 'Published' : 'Draft'}
            </div>
            <button className="pg-tb-btn" onClick={() => setIsFullscreen(f => !f)} title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
              <i className={`fa-solid ${isFullscreen ? 'fa-compress' : 'fa-expand'}`}></i>
            </button>
            <button className="pg-btn-draft" onClick={() => handleSave(false)} disabled={saving}>
              <i className="fa-regular fa-floppy-disk" style={{ marginRight: 6 }}></i>
              {saving ? 'Saving…' : 'Save Draft'}
            </button>
            <button className="pg-btn-publish" onClick={() => handleSave(true)} disabled={saving}>
              <i className="fa-solid fa-paper-plane" style={{ marginRight: 6 }}></i>
              {saving ? 'Saving…' : isEditing ? 'Update Page' : 'Publish'}
            </button>
          </div>
        </div>

        <div className="pg-workspace">
          <div className="pg-editor-col">
            <div className="pg-editor-card">

              {/* ── Quill's native toolbar (renders SVGs correctly) + custom action bar ── */}
              {!isHtmlMode && (
                <>
                  {/* Native Quill toolbar is injected here by ReactQuill automatically */}

                  {/* Premium custom action bar */}
                  <div className="pg-action-bar">
                    <button className="pg-act-btn-icon" title="Undo" onClick={() => quillExec('undo')}>
                      <i className="fa-solid fa-rotate-left"></i>
                    </button>
                    <button className="pg-act-btn-icon" title="Redo" onClick={() => quillExec('redo')}>
                      <i className="fa-solid fa-rotate-right"></i>
                    </button>
                    <div className="pg-action-sep" />

                    {/* Table */}
                    <div className="pg-table-picker-wrap">
                      <button className="pg-act-btn tb-table" title="Insert Table" onClick={() => setShowTablePicker(v => !v)}>
                        <span className="btn-icon"><i className="fa-solid fa-table-cells"></i></span>
                        Table
                      </button>
                      {showTablePicker && (
                        <div className="pg-table-picker">
                          <div style={{ fontSize: 11, color: '#5a6a7e', marginBottom: 8, fontWeight: 600 }}>
                            {tableHover.r > 0 ? `${tableHover.r} × ${tableHover.c}` : 'Hover to select'}
                          </div>
                          <div className="pg-table-grid">
                            {Array.from({ length: 6 }, (_, r) =>
                              Array.from({ length: 8 }, (_, c) => (
                                <div
                                  key={`${r}-${c}`}
                                  className={`pg-table-cell${tableHover.r > r && tableHover.c > c ? ' active' : ''}`}
                                  onMouseEnter={() => setTableHover({ r: r + 1, c: c + 1 })}
                                  onClick={() => insertTable(r + 1, c + 1)}
                                />
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <button className="pg-act-btn tb-divider" title="Insert Horizontal Rule" onClick={insertDivider}>
                      <span className="btn-icon"><i className="fa-solid fa-grip-lines"></i></span>
                      Divider
                    </button>

                    <button className="pg-act-btn tb-button" title="Insert Button" onClick={() => {
                      setEditingBtnIndex(null);
                      setBtnData({ text: 'Click Here', link: '#' });
                      setShowBtnModal(true);
                    }}>
                      <span className="btn-icon"><i className="fa-solid fa-arrow-pointer"></i></span>
                      Button
                    </button>

                    <button className="pg-act-btn tb-card" title="Insert Card" onClick={() => {
                      setEditingCardIndex(null);
                      setCardData({ icon: 'fa-solid fa-star', title: '', desc: '', color: '#CFA047', width: '300px', height: 'auto' });
                      setShowCardModal(true);
                    }}>
                      <span className="btn-icon"><i className="fa-regular fa-rectangle-list"></i></span>
                      Card
                    </button>

                    <div className="pg-action-sep" />

                    <button className={`pg-act-btn tb-find${showFindReplace ? ' active' : ''}`} title="Find & Replace" onClick={() => setShowFindReplace(v => !v)}>
                      <span className="btn-icon"><i className="fa-solid fa-magnifying-glass"></i></span>
                      Find
                    </button>

                    <div className="pg-action-bar-right">
                      <button className="pg-act-btn tb-html" onClick={() => {
                        // Capture latest Quill HTML before switching to HTML mode
                        const quill = quillRef.current?.getEditor();
                        if (quill) {
                          const html = quill.root.innerHTML;
                          setFormData(prev => ({ ...prev, content: html === '<p><br></p>' ? '' : html }));
                        }
                        setIsHtmlMode(true);
                      }} title="Switch to HTML view">
                        <span className="btn-icon"><i className="fa-solid fa-code"></i></span>
                        HTML
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* ── HTML Mode bar ── */}
              {(isHtmlMode || isHtmlPreview) && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid #dde3ea', background: isHtmlPreview ? '#0f172a' : '#1e293b', borderRadius: '14px 14px 0 0' }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {isHtmlPreview ? (
                      <><i className="fa-solid fa-eye" style={{ color: '#22c55e' }}></i>&nbsp; Visual Preview — Click to Edit</>
                    ) : (
                      <><i className="fa-solid fa-code" style={{ color: '#CFA047' }}></i> HTML Source Editor</>
                    )}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {isHtmlPreview ? (
                      <button
                        type="button"
                        onClick={() => { setIsHtmlPreview(false); setIsHtmlMode(true); }}
                        style={{ background: '#334155', color: '#e2e8f0', border: 'none', padding: '5px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        <i className="fa-solid fa-code"></i> Back to Code
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => { setIsHtmlMode(false); setIsHtmlPreview(true); }}
                        style={{ background: '#22c55e', color: '#fff', border: 'none', padding: '5px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        <i className="fa-solid fa-eye"></i> Visual View
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* ── Find & Replace bar ── */}
              {showFindReplace && !isHtmlMode && (
                <div className="pg-find-bar">
                  <span style={{ fontWeight: 600, color: '#5a6a7e' }}>Find:</span>
                  <input className="pg-find-inp" value={findText} onChange={e => setFindText(e.target.value)} placeholder="Search text…" />
                  <span style={{ fontWeight: 600, color: '#5a6a7e' }}>Replace:</span>
                  <input className="pg-find-inp" value={replaceText} onChange={e => setReplaceText(e.target.value)} placeholder="Replace with…" />
                  <button className="pg-find-btn" onClick={handleFindReplace}>Replace</button>
                  <button className="pg-find-btn" style={{ background: '#6b7a90' }} onClick={() => { setShowFindReplace(false); setFindText(''); setReplaceText(''); }}>Close</button>
                </div>
              )}

              {/* ── Editor Area ── */}
              {isHtmlPreview ? (
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: '60vh' }}>
                  {/* Visual Preview Toolbar */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 3, padding: '8px 14px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    {[['fa-rotate-left','undo'],['fa-rotate-right','redo']].map(([icon,cmd]) => (
                      <button key={cmd} title={cmd} onMouseDown={e=>{e.preventDefault();document.execCommand(cmd);}} style={{width:28,height:28,border:'1px solid #e2e8f0',borderRadius:6,background:'#fff',cursor:'pointer',fontSize:12,color:'#475569'}}>
                        <i className={`fa-solid ${icon}`}/>
                      </button>
                    ))}
                    <div style={{width:1,height:20,background:'#e2e8f0',margin:'0 3px'}}/>
                    <select title="Block" onMouseDown={e=>e.stopPropagation()} onChange={e=>{document.execCommand('formatBlock',false,e.target.value);e.target.value='';}} defaultValue="" style={{height:28,border:'1px solid #e2e8f0',borderRadius:6,background:'#fff',fontSize:12,color:'#475569',padding:'0 4px',cursor:'pointer'}}>
                      <option value="" disabled>Style</option>
                      <option value="h1">H1</option><option value="h2">H2</option><option value="h3">H3</option><option value="h4">H4</option><option value="p">Paragraph</option>
                    </select>
                    <select title="Font Size" onMouseDown={e=>e.stopPropagation()} onChange={e=>{document.execCommand('fontSize',false,e.target.value);e.target.value='';}} defaultValue="" style={{height:28,border:'1px solid #e2e8f0',borderRadius:6,background:'#fff',fontSize:12,color:'#475569',padding:'0 4px',cursor:'pointer'}}>
                      <option value="" disabled>Size</option>
                      {[['1','10px'],['2','13px'],['3','16px'],['4','18px'],['5','24px'],['6','32px'],['7','48px']].map(([v,l])=><option key={v} value={v}>{l}</option>)}
                    </select>
                    <div style={{width:1,height:20,background:'#e2e8f0',margin:'0 3px'}}/>
                    {[['fa-bold','bold'],['fa-italic','italic'],['fa-underline','underline'],['fa-strikethrough','strikeThrough']].map(([icon,cmd])=>(
                      <button key={cmd} title={cmd} onMouseDown={e=>{e.preventDefault();document.execCommand(cmd);}} style={{width:28,height:28,border:'1px solid #e2e8f0',borderRadius:6,background:'#fff',cursor:'pointer',fontSize:12,color:'#475569'}}>
                        <i className={`fa-solid ${icon}`}/>
                      </button>
                    ))}
                    <div style={{width:1,height:20,background:'#e2e8f0',margin:'0 3px'}}/>
                    <label title="Text Color" style={{display:'flex',alignItems:'center',gap:3,cursor:'pointer',border:'1px solid #e2e8f0',borderRadius:6,padding:'0 6px',height:28,background:'#fff'}}>
                      <i className="fa-solid fa-font" style={{fontSize:11,color:'#475569'}}/>
                      <input type="color" style={{width:16,height:16,border:'none',padding:0,cursor:'pointer',background:'none'}} onChange={e=>document.execCommand('foreColor',false,e.target.value)}/>
                    </label>
                    <label title="Highlight" style={{display:'flex',alignItems:'center',gap:3,cursor:'pointer',border:'1px solid #e2e8f0',borderRadius:6,padding:'0 6px',height:28,background:'#fff'}}>
                      <i className="fa-solid fa-highlighter" style={{fontSize:11,color:'#475569'}}/>
                      <input type="color" style={{width:16,height:16,border:'none',padding:0,cursor:'pointer',background:'none'}} onChange={e=>document.execCommand('backColor',false,e.target.value)}/>
                    </label>
                    <div style={{width:1,height:20,background:'#e2e8f0',margin:'0 3px'}}/>
                    {[['fa-align-left','justifyLeft'],['fa-align-center','justifyCenter'],['fa-align-right','justifyRight'],['fa-align-justify','justifyFull']].map(([icon,cmd])=>(
                      <button key={cmd} title={cmd} onMouseDown={e=>{e.preventDefault();document.execCommand(cmd);}} style={{width:28,height:28,border:'1px solid #e2e8f0',borderRadius:6,background:'#fff',cursor:'pointer',fontSize:12,color:'#475569'}}>
                        <i className={`fa-solid ${icon}`}/>
                      </button>
                    ))}
                    <div style={{width:1,height:20,background:'#e2e8f0',margin:'0 3px'}}/>
                    {[['fa-list-ul','insertUnorderedList'],['fa-list-ol','insertOrderedList'],['fa-indent','indent'],['fa-outdent','outdent']].map(([icon,cmd])=>(
                      <button key={cmd} title={cmd} onMouseDown={e=>{e.preventDefault();document.execCommand(cmd);}} style={{width:28,height:28,border:'1px solid #e2e8f0',borderRadius:6,background:'#fff',cursor:'pointer',fontSize:12,color:'#475569'}}>
                        <i className={`fa-solid ${icon}`}/>
                      </button>
                    ))}
                    <div style={{width:1,height:20,background:'#e2e8f0',margin:'0 3px'}}/>
                    <button title="Insert Link" onMouseDown={e=>{e.preventDefault();const url=window.prompt('Enter URL:','https://');if(url)document.execCommand('createLink',false,url);}} style={{width:28,height:28,border:'1px solid #e2e8f0',borderRadius:6,background:'#fff',cursor:'pointer',fontSize:12,color:'#475569'}}>
                      <i className="fa-solid fa-link"/>
                    </button>
                    <button title="Remove Link" onMouseDown={e=>{e.preventDefault();document.execCommand('unlink');}} style={{width:28,height:28,border:'1px solid #e2e8f0',borderRadius:6,background:'#fff',cursor:'pointer',fontSize:12,color:'#475569'}}>
                      <i className="fa-solid fa-link-slash"/>
                    </button>
                    <button title="Blockquote" onMouseDown={e=>{e.preventDefault();document.execCommand('formatBlock',false,'blockquote');}} style={{width:28,height:28,border:'1px solid #e2e8f0',borderRadius:6,background:'#fff',cursor:'pointer',fontSize:12,color:'#475569'}}>
                      <i className="fa-solid fa-quote-left"/>
                    </button>
                    <button title="Horizontal Rule" onMouseDown={e=>{e.preventDefault();document.execCommand('insertHorizontalRule');}} style={{width:28,height:28,border:'1px solid #e2e8f0',borderRadius:6,background:'#fff',cursor:'pointer',fontSize:12,color:'#475569'}}>
                      <i className="fa-solid fa-grip-lines"/>
                    </button>
                    <button title="Clear Formatting" onMouseDown={e=>{e.preventDefault();document.execCommand('removeFormat');}} style={{width:28,height:28,border:'1px solid #e2e8f0',borderRadius:6,background:'#fff',cursor:'pointer',fontSize:12,color:'#ef4444'}}>
                      <i className="fa-solid fa-eraser"/>
                    </button>
                  </div>
                  {/* Editable content area */}
                  <div
                    ref={(el) => {
                      if (el && el.getAttribute('data-init') !== '1') {
                        el.innerHTML = formData.content || '';
                        el.setAttribute('data-init', '1');
                      }
                    }}
                    style={{ flex: 1, minHeight: '60vh', background: '#ffffff', overflowY: 'auto', padding: '32px 40px', outline: 'none', cursor: 'text', lineHeight: 1.8, fontFamily: 'Inter, sans-serif', fontSize: 15 }}
                    contentEditable
                    suppressContentEditableWarning
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.opacity = '0.7'; }}
                    onDragLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.style.opacity = '1';
                      const textData = e.dataTransfer.getData('text/plain');
                      if (textData) {
                        let embedHtml = null;
                        if (textData.startsWith('[FORM:')) {
                          const match = textData.match(/\[FORM:(\d+):([^\]]+)\]/);
                          if (match) {
                            const formId = match[1], formSlug = match[2];
                            const formName = availableForms.find(f => String(f.id) === formId)?.name || formSlug;
                            embedHtml = `<a href="#form:${formId}" contenteditable="false" style="display:block;border:2px dashed #CFA047;border-radius:12px;padding:20px 24px;margin:16px 0;background:linear-gradient(135deg,#fdf5e6,#fffbf2);text-decoration:none;font-family:'Inter',sans-serif;cursor:default;"><div style="display:flex;align-items:center;gap:16px;"><span style="width:44px;height:44px;border-radius:11px;background:linear-gradient(135deg,#061830,#CFA047);display:inline-flex;align-items:center;justify-content:center;color:white;font-size:18px;flex-shrink:0;"><i class="fa-solid fa-clipboard-list"></i></span><span><strong style="font-size:15px;color:#1a2332;display:block;">${formName}</strong><span style="font-size:12px;color:#9babbf;">Form Embed &nbsp;&middot;&nbsp; /${formSlug}</span></span></div></a>`;
                          }
                        } else if (textData.startsWith('[PACKAGE:')) {
                          const match = textData.match(/\[PACKAGE:(\d+):([^\]]+)\]/);
                          if (match) {
                            const packageId = match[1], packageName = match[2];
                            embedHtml = `<a href="#package:${packageId}" contenteditable="false" style="display:block;border:2px dashed #10b981;border-radius:12px;padding:20px 24px;margin:16px 0;background:linear-gradient(135deg,#ecfdf5,#f0fdf4);text-decoration:none;font-family:'Inter',sans-serif;cursor:default;"><div style="display:flex;align-items:center;gap:16px;"><span style="width:44px;height:44px;border-radius:11px;background:linear-gradient(135deg,#059669,#10b981);display:inline-flex;align-items:center;justify-content:center;color:white;font-size:18px;flex-shrink:0;"><i class="fa-solid fa-box-open"></i></span><span><strong style="font-size:15px;color:#1a2332;display:block;">${packageName}</strong><span style="font-size:12px;color:#64748b;">Pricing Package Embed &nbsp;&middot;&nbsp; ID: ${packageId}</span></span></div></a>`;
                          }
                        }
                        
                        if (embedHtml) {
                          // Make sure the div is focused so execCommand works at cursor pos
                          e.currentTarget.focus();
                          document.execCommand('insertHTML', false, embedHtml);
                        } else {
                          document.execCommand('insertText', false, textData);
                        }
                      }
                      const updatedHtml = e.currentTarget.innerHTML;
                      setFormData(prev => ({ ...prev, content: updatedHtml }));
                    }}
                    onInput={(e) => {
                      const newHtml = e.currentTarget.innerHTML;
                      setFormData(prev => ({ ...prev, content: newHtml }));
                    }}
                  />
                </div>
              ) : isHtmlMode ? (
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: '60vh' }}>
                  <div style={{ padding: '6px 16px', background: '#1e293b', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }}></span>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b', display: 'inline-block' }}></span>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }}></span>
                    <span style={{ fontSize: 11, color: '#64748b', marginLeft: 8 }}>index.html</span>
                  </div>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                    style={{ flex: 1, width: '100%', padding: '20px 28px', fontFamily: '"Fira Code", Consolas, monospace', fontSize: '13.5px', border: 'none', outline: 'none', resize: 'none', background: '#0f172a', color: '#e2e8f0', lineHeight: '1.7', minHeight: '60vh', tabSize: 2 }}
                    placeholder="<p>Write your raw HTML here...</p>"
                    onKeyDown={(e) => {
                      if (e.key === 'Tab') {
                        e.preventDefault();
                        const s = e.target.selectionStart, en = e.target.selectionEnd;
                        const v = formData.content.substring(0, s) + '  ' + formData.content.substring(en);
                        setFormData(prev => ({ ...prev, content: v }));
                        setTimeout(() => { e.target.selectionStart = e.target.selectionEnd = s + 2; }, 0);
                      }
                    }}
                  />
                </div>
              ) : (
                <div
                  className="pg-drop-zone"
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('dragging'); }}
                  onDragLeave={(e) => { e.currentTarget.classList.remove('dragging'); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('dragging');

                    // Handle form card drop
                    const textData = e.dataTransfer.getData('text/plain');
                    if (textData && textData.startsWith('[FORM:')) {
                      const match = textData.match(/\[FORM:(\d+):([^\]]+)\]/);
                      if (match) {
                        const formId = match[1];
                        const formSlug = match[2];
                        const formName = availableForms.find(f => String(f.id) === formId)?.name || formSlug;
                        const quill = quillRef.current?.getEditor();
                        if (quill) {
                          const range = quill.getSelection(true) || { index: quill.getLength() };
                          const embedHtml = `<a href="#form:${formId}" contenteditable="false" style="display:block;border:2px dashed #CFA047;border-radius:12px;padding:20px 24px;margin:16px 0;background:linear-gradient(135deg,#fdf5e6,#fffbf2);text-decoration:none;font-family:'Inter',sans-serif;cursor:default;"><div style="display:flex;align-items:center;gap:16px;"><span style="width:44px;height:44px;border-radius:11px;background:linear-gradient(135deg,#061830,#CFA047);display:inline-flex;align-items:center;justify-content:center;color:white;font-size:18px;flex-shrink:0;"><i class="fa-solid fa-clipboard-list"></i></span><span><strong style="font-size:15px;color:#1a2332;display:block;">${formName}</strong><span style="font-size:12px;color:#9babbf;">Form Embed &nbsp;&middot;&nbsp; /${formSlug}</span></span></div></a>`;
                          quill.clipboard.dangerouslyPasteHTML(range.index, embedHtml);
                          quill.setSelection(range.index + 1);
                        }
                        return;
                      }
                    }

                    if (textData && textData.startsWith('[PACKAGE:')) {
                      const packageMatch = textData.match(/\[PACKAGE:(\d+):([^\]]+)\]/);
                      if (packageMatch) {
                        const packageId = packageMatch[1];
                        const packageName = packageMatch[2];
                        const quill = quillRef.current?.getEditor();
                        if (quill) {
                          const range = quill.getSelection(true) || { index: quill.getLength() };
                          const embedHtml = `<a href="#package:${packageId}" contenteditable="false" style="display:block;border:2px dashed #10b981;border-radius:12px;padding:20px 24px;margin:16px 0;background:linear-gradient(135deg,#ecfdf5,#f0fdf4);text-decoration:none;font-family:'Inter',sans-serif;cursor:default;"><div style="display:flex;align-items:center;gap:16px;"><span style="width:44px;height:44px;border-radius:11px;background:linear-gradient(135deg,#059669,#10b981);display:inline-flex;align-items:center;justify-content:center;color:white;font-size:18px;flex-shrink:0;"><i class="fa-solid fa-box-open"></i></span><span><strong style="font-size:15px;color:#1a2332;display:block;">${packageName}</strong><span style="font-size:12px;color:#64748b;">Pricing Package Embed &nbsp;&middot;&nbsp; ID: ${packageId}</span></span></div></a>`;
                          quill.clipboard.dangerouslyPasteHTML(range.index, embedHtml);
                          quill.setSelection(range.index + 1);
                        }
                        return;
                      }
                    }

                    // Handle image file drop
                    Array.from(e.dataTransfer.files).forEach(f => insertImageFile(f));
                  }}
                >
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    modules={quillModules}
                    value={formData.content}
                    onChange={(val) => {
                      setFormData(prev => ({ ...prev, content: val }));
                      updateCounts(val);
                    }}
                    placeholder="Start writing your page content… or drag & drop images here"
                    style={{ display: 'flex', flexDirection: 'column', flex: 1 }}
                  />
                </div>
              )}

              {/* ── Word / Char count bar ── */}
              {!isHtmlMode && (
                <div className="pg-wordcount">
                  <span><i className="fa-solid fa-file-word" style={{ color: '#CFA047', marginRight: 4 }}></i>{wordCount} words</span>
                  <span><i className="fa-solid fa-font" style={{ color: '#8496a9', marginRight: 4 }}></i>{charCount} characters</span>
                  <span style={{ color: '#bcc7d4' }}>Drag & drop images to place them anywhere</span>
                </div>
              )}

            </div>
          </div>

          {/* Settings Panel */}
          <div className="pg-settings-panel">
            <div className="pg-panel-tabs">
              {[
                { id: 'publish', icon: 'fa-circle-dot', label: 'Status' },
                { id: 'menu', icon: 'fa-bars', label: 'Menu' },
                { id: 'integrations', icon: 'fa-plug', label: 'Links' },
                { id: 'seo', icon: 'fa-magnifying-glass', label: 'SEO' },
              ].map(tab => (
                <button
                  key={tab.id}
                  className={`pg-panel-tab${activeSection === tab.id ? ' active' : ''}`}
                  onClick={() => setActiveSection(tab.id)}
                >
                  <i className={`fa-solid ${tab.icon}`}></i>
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="pg-panel-body">

              {activeSection === 'publish' && (
                <>
                  <div className="pg-field-group">
                    <div className="pg-toggle-row">
                      <span className="pg-toggle-label">Published</span>
                      <label className="pg-switch">
                        <input type="checkbox" name="is_published" checked={formData.is_published} onChange={handleChange} />
                        <span className="pg-slider"></span>
                      </label>
                    </div>
                    <p className="pg-field-hint">When enabled, the page is visible to the public.</p>
                  </div>
                </>
              )}

              {activeSection === 'menu' && (
                <>
                  <div className="pg-field-group">
                    <label className="pg-field-label">URL Slug</label>
                    <input type="text" className="pg-inp" name="slug" value={formData.slug} onChange={handleChange} placeholder="e.g. about-us" />
                    <p className="pg-field-hint">Auto-generated from title if left blank.</p>
                  </div>
                  <div className="pg-divider"></div>
                  <div className="pg-field-group">
                    <label className="pg-field-label">Menu Title (Navbar Category) <span style={{color: 'red'}}>*</span></label>
                    <select className="pg-inp" name="menu_title" value={formData.menu_title || ''} onChange={handleChange}>
                      <option value="">Select a category...</option>
                      {primaryLinks.map((link, idx) => (
                        <option key={idx} value={link.label}>{link.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="pg-field-group">
                    <div className="pg-toggle-row">
                      <span className="pg-toggle-label">Show in Navigation</span>
                      <label className="pg-switch">
                        <input type="checkbox" name="show_in_menu" checked={formData.show_in_menu} onChange={handleChange} />
                        <span className="pg-slider"></span>
                      </label>
                    </div>
                  </div>
                  <div className="pg-field-group">
                    <label className="pg-field-label">Sort Order</label>
                    <input type="number" className="pg-inp" name="sort_order" value={formData.sort_order} onChange={handleChange} />
                    <p className="pg-field-hint">Lower numbers appear first in menus.</p>
                  </div>
                </>
              )}

              {activeSection === 'integrations' && (
                <>
                  <div className="fp-section-label" style={{ marginBottom: 4 }}>Available Forms</div>
                  <p className="pg-field-hint" style={{ marginBottom: 10 }}>Drag &amp; drop a form card directly into the editor to embed it.</p>
                  <div className="fp-forms-list">
                    {availableForms.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '16px', color: '#9babbf', fontSize: '12px' }}>
                        No forms found. Create forms in the Forms section.
                      </div>
                    ) : availableForms.map(form => (
                      <div
                        key={form.id}
                        className="fp-form-card"
                        draggable
                        onDragStart={e => {
                          dragFormId.current = String(form.id);
                          e.dataTransfer.effectAllowed = 'copy';
                          // Store form info for editor drop
                          e.dataTransfer.setData('text/plain', `[FORM:${form.id}:${form.slug}]`);
                        }}
                        onDragEnd={() => { dragFormId.current = null; }}
                      >
                        <div className="fp-form-card-icon"><i className="fa-solid fa-table-list"></i></div>
                        <div className="fp-form-card-info">
                          <div className="fp-form-card-name">{form.name}</div>
                          <div className="fp-form-card-meta">
                            {form.schema ? form.schema.length : 0} fields &nbsp;·&nbsp; /{form.slug}
                          </div>
                        </div>
                        <div className="fp-form-card-drag"><i className="fa-solid fa-grip-vertical"></i></div>
                      </div>
                    ))}
                  </div>

                  <div className="fp-section-label" style={{ marginTop: 24, marginBottom: 4 }}>Available Packages</div>
                  <div className="fp-forms-list">
                    {availablePackages.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '16px', color: '#9babbf', fontSize: '12px' }}>
                        No packages found. Create packages in the Packages section.
                      </div>
                    ) : availablePackages.map(pkg => (
                      <div
                        key={pkg.id}
                        className="fp-form-card"
                        draggable
                        onDragStart={e => {
                          e.dataTransfer.effectAllowed = 'copy';
                          e.dataTransfer.setData('text/plain', `[PACKAGE:${pkg.id}:${pkg.name}]`);
                        }}
                      >
                        <div className="fp-form-card-icon" style={{background:'rgba(16,185,129,0.1)', color:'#10b981', border:'1px solid rgba(16,185,129,0.2)'}}><i className="fa-solid fa-tags"></i></div>
                        <div className="fp-form-card-info">
                          <div className="fp-form-card-name">{pkg.name}</div>
                          <div className="fp-form-card-meta">
                            {pkg.children?.length || 0} sub-packages
                          </div>
                        </div>
                        <div className="fp-form-card-drag"><i className="fa-solid fa-grip-vertical"></i></div>
                      </div>
                    ))}
                  </div>
                </>
              )}


              {activeSection === 'seo' && (
                <>
                  <div className="pg-field-group">
                    <label className="pg-field-label">Meta Title</label>
                    <input type="text" className="pg-inp" name="meta_title" value={formData.meta_title} onChange={handleChange} placeholder="Page title for search engines" />
                  </div>
                  <div className="pg-field-group">
                    <label className="pg-field-label">Meta Description</label>
                    <textarea className="pg-inp pg-textarea" name="meta_description" value={formData.meta_description} onChange={handleChange} placeholder="Brief description shown in search results (160 chars max)"></textarea>
                  </div>
                  <div className="pg-field-group">
                    <label className="pg-field-label">Meta Keywords</label>
                    <textarea className="pg-inp pg-textarea" style={{ minHeight: '60px' }} name="meta_keywords" value={formData.meta_keywords} onChange={handleChange} placeholder="tax, filing, accounting…"></textarea>
                  </div>
                </>
              )}

            </div>
          </div>
        </div>

        {/* ── Card Builder Modal ── */}
        {showCardModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
            <div style={{ background: '#fff', width: '500px', borderRadius: '16px', boxShadow: '0 20px 40px rgba(0,0,0,0.1)', overflow: 'hidden', fontFamily: "'Inter', sans-serif" }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="fa-regular fa-id-card" style={{ color: '#CFA047' }}></i> Build Custom Card
                </h3>
                <button onClick={() => setShowCardModal(false)} style={{ background: 'none', border: 'none', fontSize: '20px', color: '#64748b', cursor: 'pointer' }}>&times;</button>
              </div>
              <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Icon Class (FontAwesome)</label>
                    <input type="text" className="pg-inp" value={cardData.icon} onChange={e => setCardData({ ...cardData, icon: e.target.value })} placeholder="fa-solid fa-star" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Theme Color</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input type="color" value={cardData.color} onChange={e => setCardData({ ...cardData, color: e.target.value })} style={{ width: '36px', height: '36px', padding: '0', border: 'none', borderRadius: '6px', cursor: 'pointer' }} />
                      <input type="text" className="pg-inp" value={cardData.color} onChange={e => setCardData({ ...cardData, color: e.target.value })} style={{ flex: 1 }} />
                    </div>
                  </div>
                </div>
                
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Card Title</label>
                  <input type="text" className="pg-inp" value={cardData.title} onChange={e => setCardData({ ...cardData, title: e.target.value })} placeholder="Title..." />
                </div>
                
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Description</label>
                  <textarea className="pg-inp pg-textarea" value={cardData.desc} onChange={e => setCardData({ ...cardData, desc: e.target.value })} placeholder="Card content..." style={{ minHeight: '80px' }}></textarea>
                </div>

                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Width</label>
                    <input type="text" className="pg-inp" value={cardData.width} onChange={e => setCardData({ ...cardData, width: e.target.value })} placeholder="100% or 300px" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Min Height</label>
                    <input type="text" className="pg-inp" value={cardData.height} onChange={e => setCardData({ ...cardData, height: e.target.value })} placeholder="auto or 200px" />
                  </div>
                </div>

              </div>
              <div style={{ padding: '16px 24px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button onClick={() => { setShowCardModal(false); setEditingCardIndex(null); }} style={{ padding: '8px 16px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', color: '#475569', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                <button onClick={insertCardEmbed} style={{ padding: '8px 20px', background: 'linear-gradient(135deg, #CFA047 0%, #061830 100%)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(207,160,71,0.3)' }}>{editingCardIndex !== null ? 'Update Card' : 'Insert Card'}</button>
              </div>
            </div>
          </div>
        )}
        {/* ── Button Builder Modal ── */}
        {showBtnModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
            <div style={{ background: '#fff', width: '400px', borderRadius: '16px', boxShadow: '0 20px 40px rgba(0,0,0,0.1)', overflow: 'hidden', fontFamily: "'Inter', sans-serif" }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="fa-solid fa-link" style={{ color: '#CFA047' }}></i> Build Button
                </h3>
                <button onClick={() => setShowBtnModal(false)} style={{ background: 'none', border: 'none', fontSize: '20px', color: '#64748b', cursor: 'pointer' }}>&times;</button>
              </div>
              <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Button Text</label>
                  <input type="text" className="pg-inp" value={btnData.text} onChange={e => setBtnData({ ...btnData, text: e.target.value })} placeholder="Click Here" />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Target Link (URL or #hash)</label>
                  <input type="text" className="pg-inp" value={btnData.link} onChange={e => setBtnData({ ...btnData, link: e.target.value })} placeholder="/#contact" />
                </div>
              </div>
              <div style={{ padding: '16px 24px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button onClick={() => { setShowBtnModal(false); setEditingBtnIndex(null); }} style={{ padding: '8px 16px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', color: '#475569', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                <button onClick={insertButtonEmbed} style={{ padding: '8px 20px', background: 'linear-gradient(135deg, #CFA047 0%, #061830 100%)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(207,160,71,0.3)' }}>{editingBtnIndex !== null ? 'Update Button' : 'Insert Button'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── LIST VIEW ──────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        .pg-list-wrap { font-family: 'Inter', sans-serif; }

        /* Hero Banner */
        .pg-hero {
          background: linear-gradient(135deg, #040f1f 0%, #061830 40%, #CFA047 100%);
          border-radius: 18px; padding: 32px 36px; margin-bottom: 28px;
          display: flex; align-items: center; justify-content: space-between;
          position: relative; overflow: hidden;
        }
        .pg-hero::before {
          content: ''; position: absolute; top: -40px; right: -40px;
          width: 240px; height: 240px; border-radius: 50%;
          background: rgba(255,255,255,0.05);
        }
        .pg-hero::after {
          content: ''; position: absolute; bottom: -60px; right: 120px;
          width: 180px; height: 180px; border-radius: 50%;
          background: rgba(255,255,255,0.04);
        }
        .pg-hero-left { position: relative; z-index: 1; }
        .pg-hero-eyebrow { font-size: 11px; font-weight: 700; color: #CFA047; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
        .pg-hero-title { font-size: 26px; font-weight: 800; color: #ffffff; margin: 0 0 8px; line-height: 1.2; }
        .pg-hero-sub { font-size: 14px; color: rgba(255,255,255,0.8); margin: 0; }
        .pg-hero-right { position: relative; z-index: 1; display: flex; align-items: center; gap: 16px; }
        .pg-hero-stat { background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.15); border-radius: 14px; padding: 16px 22px; text-align: center; backdrop-filter: blur(6px); }
        .pg-hero-stat-val { font-size: 28px; font-weight: 800; color: #ffffff; line-height: 1; }
        .pg-hero-stat-label { font-size: 11px; color: #CFA047; font-weight: 600; margin-top: 4px; }

        .pg-create-btn {
          display: flex; align-items: center; gap: 8px;
          padding: 11px 22px; border-radius: 10px; border: none;
          background: #ffffff;
          color: #061830; font-size: 14px; font-weight: 700;
          cursor: pointer; font-family: 'Inter', sans-serif;
          box-shadow: 0 4px 14px rgba(0,0,0,0.15); transition: all 0.2s;
          white-space: nowrap; flex-shrink: 0;
        }
        .pg-create-btn:hover { box-shadow: 0 6px 18px rgba(207, 160, 71,0.4); transform: translateY(-1px); }

        /* Error banner */
        .pg-error { background: #fff1f2; border: 1px solid #fecdd3; border-radius: 10px; padding: 12px 16px; color: #e11d48; font-size: 13px; margin-bottom: 20px; display: flex; align-items: center; gap: 10px; }

        /* Stats Row */
        .pg-stats-row { display: flex; gap: 16px; margin-bottom: 24px; }
        .pg-stat-card {
          flex: 1; background: #ffffff; border-radius: 12px;
          border: 1px solid #dde3ea; padding: 16px 20px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.04);
        }
        .pg-stat-val { font-size: 28px; font-weight: 700; color: #1a2332; line-height: 1; }
        .pg-stat-label { font-size: 12px; color: #8496a9; font-weight: 500; margin-top: 4px; }

        /* Table card */
        .pg-table-card {
          background: #ffffff; border-radius: 14px;
          border: 1px solid #dde3ea;
          box-shadow: 0 2px 12px rgba(0,0,0,0.05); overflow: hidden;
        }
        .pg-table-toolbar {
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 20px; border-bottom: 1px solid #eef1f5;
        }
        .pg-table-toolbar-title { font-size: 14px; font-weight: 600; color: #2d3a4b; }
        .pg-count-badge { background: #f0f4f8; color: #5a6a7e; font-size: 12px; font-weight: 600; padding: 3px 10px; border-radius: 20px; margin-left: 8px; }

        table.pg-table { width: 100%; border-collapse: collapse; }
        .pg-table thead tr { background: #f7f9fc; }
        .pg-table th { padding: 13px 18px; font-size: 11.5px; font-weight: 700; color: #8496a9; text-transform: uppercase; letter-spacing: 0.6px; border-bottom: 1px solid #eef1f5; white-space: nowrap; }
        .pg-table td { padding: 15px 18px; font-size: 13.5px; color: #3d4f62; border-bottom: 1px solid #f0f4f8; vertical-align: middle; }
        .pg-table tbody tr:last-child td { border-bottom: none; }
        .pg-table tbody tr { transition: background 0.12s; }
        .pg-table tbody tr:hover td { background: #f7f9fc; }

        /* Cell elements */
        .pg-page-name { font-weight: 600; color: #1a2332; font-size: 14px; }
        .pg-page-desc { font-size: 12px; color: #9babbf; margin-top: 2px; }
        .pg-slug-chip {
          display: inline-flex; align-items: center; gap: 5px;
          background: #f0f4f8; border: 1px solid #dde3ea;
          padding: 4px 10px; border-radius: 6px; font-size: 12px;
          color: #5a6a7e; font-family: 'Courier New', monospace;
        }

        .pg-badge { display: inline-flex; align-items: center; gap: 5px; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
        .pg-badge.pub { background: #f8fafc; color: #061830; }
        .pg-badge.draft { background: #fffbeb; color: #d97706; }
        .pg-badge.menu { background: #eff6ff; color: #3b82f6; }
        .pg-badge.hidden { background: #f8fafc; color: #94a3b8; }
        .pg-badge-dot { width: 6px; height: 6px; border-radius: 50%; }
        .pub .pg-badge-dot { background: #CFA047; }
        .draft .pg-badge-dot { background: #f59e0b; }
        .menu .pg-badge-dot { background: #3b82f6; }
        .hidden .pg-badge-dot { background: #cbd5e1; }

        /* Action buttons */
        .pg-actions { display: flex; gap: 6px; justify-content: flex-end; }
        .pg-act-btn {
          display: flex; align-items: center; justify-content: center;
          width: 34px; height: 34px; border-radius: 8px;
          border: 1.5px solid #dde3ea; background: #f7f9fc;
          color: #6b7a90; cursor: pointer; font-size: 13px;
          transition: all 0.18s;
        }
        .pg-act-btn:hover { background: #ffffff; border-color: #bcc7d4; color: #1a2332; box-shadow: 0 2px 6px rgba(0,0,0,0.08); }
        .pg-act-btn.edit:hover { border-color: #CFA047; color: #CFA047; background: #f8fafc; }
        .pg-act-btn.del:hover { border-color: #fca5a5; color: #ef4444; background: #fff1f2; }

        /* Empty state */
        .pg-empty { text-align: center; padding: 60px 24px; }
        .pg-empty-icon { font-size: 40px; color: #dde3ea; margin-bottom: 16px; }
        .pg-empty-title { font-size: 16px; font-weight: 600; color: #5a6a7e; margin-bottom: 6px; }
        .pg-empty-text { font-size: 13px; color: #9babbf; }

        /* Skeleton */
        .pg-skel { height: 14px; border-radius: 6px; background: linear-gradient(90deg, #f0f4f8 25%, #e4eaf1 50%, #f0f4f8 75%); background-size: 200% 100%; animation: shimmer 1.4s infinite; }
        @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
      `}</style>

      <div className="pg-list-wrap">
        {/* Hero Banner */}
        <div className="pg-hero">
          <div className="pg-hero-left">
            <div className="pg-hero-eyebrow"><i className="fa-solid fa-file-lines"></i> Page Management</div>
            <h2 className="pg-hero-title">Your Website Pages</h2>
            <p className="pg-hero-sub">Build, publish and manage all the pages that appear on your website.</p>
          </div>
          <div className="pg-hero-right">
            {!loading && (
              <>
                <div className="pg-hero-stat">
                  <div className="pg-hero-stat-val">{pages.length}</div>
                  <div className="pg-hero-stat-label">Total</div>
                </div>
                <div className="pg-hero-stat">
                  <div className="pg-hero-stat-val">{pages.filter(p => p.is_published).length}</div>
                  <div className="pg-hero-stat-label">Published</div>
                </div>
              </>
            )}
            <button className="pg-create-btn" onClick={handleOpenCreate}>
              <i className="fa-solid fa-plus"></i>
              New Page
            </button>
          </div>
        </div>

        {error && (
          <div className="pg-error">
            <i className="fa-solid fa-circle-exclamation"></i>
            {error}
          </div>
        )}

        <div className="pg-table-card">
          <div className="pg-table-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span className="pg-table-toolbar-title">All Pages</span>
              <span className="pg-count-badge">{filteredPages.length}</span>
            </div>
            <div style={{ position: 'relative' }}>
              <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9babbf', fontSize: '13px' }}></i>
              <input 
                type="text" 
                placeholder="Search pages..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ padding: '8px 12px 8px 32px', borderRadius: '8px', border: '1px solid #dde3ea', outline: 'none', fontSize: '13px', fontFamily: "'Inter', sans-serif", width: '240px', background: '#f9fbfc' }}
              />
            </div>
          </div>

          <table className="pg-table">
            <thead>
              <tr>
                <th>Page</th>
                <th>URL Slug</th>
                <th>Menu</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [1,2,3].map(i => (
                  <tr key={i}>
                    {[200, 120, 80, 70, 60].map((w, j) => (
                      <td key={j}><div className="pg-skel" style={{ width: w }}></div></td>
                    ))}
                  </tr>
                ))
              ) : filteredPages.length === 0 ? (
                <tr>
                  <td colSpan="5">
                    <div className="pg-empty">
                      <div className="pg-empty-icon"><i className="fa-regular fa-file-lines"></i></div>
                      <div className="pg-empty-title">{pages.length === 0 ? 'No pages yet' : 'No matching pages found'}</div>
                      <div className="pg-empty-text">{pages.length === 0 ? 'Click "New Page" to create your first page.' : 'Try adjusting your search filter.'}</div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredPages.map(page => (
                  <tr key={page.id}>
                    <td>
                      <div className="pg-page-name">{page.title}</div>
                      {page.menu_title && page.menu_title !== page.title && (
                        <div className="pg-page-desc">Nav: {page.menu_title}</div>
                      )}
                    </td>
                    <td>
                      <span className="pg-slug-chip">
                        <i className="fa-solid fa-link" style={{ fontSize: '10px', color: '#9babbf' }}></i>
                        /{page.slug}
                      </span>
                    </td>
                    <td>
                      <span className={`pg-badge ${page.show_in_menu ? 'menu' : 'hidden'}`}>
                        <span className="pg-badge-dot"></span>
                        {page.show_in_menu ? 'In Menu' : 'Hidden'}
                      </span>
                    </td>
                    <td>
                      <span className={`pg-badge ${page.is_published ? 'pub' : 'draft'}`}>
                        <span className="pg-badge-dot"></span>
                        {page.is_published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td>
                      <div className="pg-actions">
                        <button className="pg-act-btn edit" onClick={() => handleOpenEdit(page)} title="Edit Page">
                          <i className="fa-solid fa-pen-to-square"></i>
                        </button>
                        <button className="pg-act-btn del" onClick={() => confirmDelete(page)} title="Delete">
                          <i className="fa-solid fa-trash-can"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {modalConfig.isOpen && (
          <div className="sv-overlay" onClick={() => setModalConfig({ ...modalConfig, isOpen: false })} style={{ alignItems: 'center', position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 1000, display: 'flex', justifyContent: 'center' }}>
            <div onClick={e => e.stopPropagation()} style={{ background: 'white', padding: '28px 24px', borderRadius: '16px', width: '320px', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>{modalConfig.title}</h3>
              <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 24px', lineHeight: 1.5 }}>{modalConfig.message}</p>
              <button style={{ width: '100%', padding: '12px', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', border: 'none', background: '#CFA047', color: 'white' }} onClick={() => setModalConfig({ ...modalConfig, isOpen: false })}>OK</button>
            </div>
          </div>
        )}

        {itemToDelete && (
          <div className="sv-overlay" onClick={() => setItemToDelete(null)} style={{ alignItems: 'center', position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 1000, display: 'flex', justifyContent: 'center' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', padding: '28px 24px', borderRadius: '16px', width: '320px', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#fee2e2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', margin: '0 auto 16px' }}>
                <i className="fa-solid fa-triangle-exclamation"></i>
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>Delete Page</h3>
              <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 24px', lineHeight: 1.5 }}>
                Are you sure you want to delete the page <strong>"{itemToDelete.title}"</strong>? This action cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button style={{ flex: 1, padding: '12px', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', border: 'none', background: '#f1f5f9', color: '#475569' }} onClick={() => setItemToDelete(null)}>Cancel</button>
                <button style={{ flex: 1, padding: '12px', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', border: 'none', background: '#ef4444', color: 'white' }} onClick={executeDelete}>Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminPages;
